import React, { useState } from "react";
import { FiDroplet, FiAlertTriangle, FiCheckCircle, FiThermometer, FiWind } from "react-icons/fi";
import { MdMale, MdFemale, MdTransgender } from "react-icons/md";
import { SectionHeader } from "../../../components/Shared";
import { MiniBar } from "./facilityHelpers";

const STATUS_META = {
  "Clean":        { color: "#10b981", bg: "#f0fdf4", border: "#10b98140" },
  "Attention":    { color: "#f59e0b", bg: "#fffbeb", border: "#f59e0b40" },
  "Needs Service":{ color: "#ef4444", bg: "#fef2f2", border: "#ef444440" },
};

const TypeIcon = ({ type, size = 18 }) => {
  if (type === "Male")   return <MdMale   size={size} />;
  if (type === "Female") return <MdFemale size={size} />;
  return <MdTransgender size={size} />;
};

const typeColor = (type) =>
  type === "Male" ? "#3b82f6" : type === "Female" ? "#ec4899" : "#8b5cf6";

const supplyColor = (v) => v <= 20 ? "#ef4444" : v <= 40 ? "#f59e0b" : "#10b981";
const odorColor  = (v) => v >= 50 ? "#ef4444" : v >= 25 ? "#f59e0b" : "#10b981";

const FLOOR_FILTERS = ["All", "G/F", "Floor 1", "Floor 3", "Floor 5", "Cafeteria"];

const RestroomPanel = ({ d }) => {
  const [floorFilter, setFloorFilter] = useState("All");

  const filtered = d.restrooms.filter(r =>
    floorFilter === "All" || r.floor === floorFilter
  );

  const totalAlerts = d.restrooms.reduce((n, r) => n + r.alerts.length, 0);
  const needsService = d.restrooms.filter(r => r.cleaningStatus === "Needs Service").length;
  const attention    = d.restrooms.filter(r => r.cleaningStatus === "Attention").length;
  const clean        = d.restrooms.filter(r => r.cleaningStatus === "Clean").length;

  return (
    <div>
      {/* Summary strip */}
      <div className="fm-rr-summary-row">
        <div className="fm-rr-summary-card" style={{ borderColor: "#10b98140" }}>
          <FiCheckCircle size={22} color="#10b981" />
          <div className="fm-rr-summary-val" style={{ color: "#10b981" }}>{clean}</div>
          <div className="fm-rr-summary-lbl">Clean</div>
        </div>
        <div className="fm-rr-summary-card" style={{ borderColor: "#f59e0b40" }}>
          <FiAlertTriangle size={22} color="#f59e0b" />
          <div className="fm-rr-summary-val" style={{ color: "#f59e0b" }}>{attention}</div>
          <div className="fm-rr-summary-lbl">Attention Needed</div>
        </div>
        <div className="fm-rr-summary-card" style={{ borderColor: "#ef444440" }}>
          <FiAlertTriangle size={22} color="#ef4444" />
          <div className="fm-rr-summary-val" style={{ color: "#ef4444" }}>{needsService}</div>
          <div className="fm-rr-summary-lbl">Needs Service</div>
        </div>
        <div className="fm-rr-summary-card" style={{ borderColor: "#667eea40" }}>
          <FiAlertTriangle size={22} color="#667eea" />
          <div className="fm-rr-summary-val" style={{ color: "#667eea" }}>{totalAlerts}</div>
          <div className="fm-rr-summary-lbl">Active Alerts</div>
        </div>
      </div>

      {/* Floor filter */}
      <div className="fm-tracking-filter">
        {FLOOR_FILTERS.map(f => (
          <button key={f}
            className={`fm-filter-chip ${floorFilter === f ? "fm-filter-chip-active" : ""}`}
            onClick={() => setFloorFilter(f)}>
            {f}
            <span className="fm-filter-count">
              {f === "All" ? d.restrooms.length : d.restrooms.filter(r => r.floor === f).length}
            </span>
          </button>
        ))}
      </div>

      {/* Restroom cards */}
      <SectionHeader title="Restroom Status" />
      <div className="fm-rr-grid">
        {filtered.map(r => {
          const sm = STATUS_META[r.cleaningStatus] || STATUS_META["Clean"];
          const tc = typeColor(r.type);
          return (
            <div key={r.id} className="fm-rr-card"
              style={{ borderColor: sm.border, background: sm.bg }}>

              {/* Card header */}
              <div className="fm-rr-card-header">
                <div className="fm-rr-type-icon" style={{ background: `${tc}18`, color: tc }}>
                  <TypeIcon type={r.type} size={20} />
                </div>
                <div className="fm-rr-card-title">
                  <div className="fm-rr-name">{r.name}</div>
                  <div className="fm-rr-floor">{r.floor}</div>
                </div>
                <div className="fm-rr-badges">
                  <span className={`fm-rr-occ-badge ${r.occupied ? "rr-occupied" : "rr-vacant"}`}>
                    {r.occupied ? "Occupied" : "Vacant"}
                  </span>
                  <span className="fm-rr-clean-badge" style={{ background: `${sm.color}18`, color: sm.color }}>
                    {r.cleaningStatus}
                  </span>
                </div>
              </div>

              {/* Alerts */}
              {r.alerts.length > 0 && (
                <div className="fm-rr-alerts">
                  {r.alerts.map((a, i) => (
                    <div key={i} className="fm-rr-alert-item">
                      <FiAlertTriangle size={11} />
                      <span>{a}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Supply levels */}
              <div className="fm-rr-supplies">
                <div className="fm-rr-supply-row">
                  <div className="fm-rr-supply-icon" style={{ color: supplyColor(r.sanitizerLevel) }}>
                    <FiDroplet size={13} />
                  </div>
                  <span className="fm-rr-supply-lbl">Sanitizer</span>
                  <MiniBar value={r.sanitizerLevel} max={100} height={5}
                    color={supplyColor(r.sanitizerLevel)} />
                  <span className="fm-rr-supply-val" style={{ color: supplyColor(r.sanitizerLevel) }}>
                    {r.sanitizerLevel}%
                  </span>
                </div>
                <div className="fm-rr-supply-row">
                  <div className="fm-rr-supply-icon" style={{ color: supplyColor(r.paperLevel) }}>
                    <span style={{ fontSize: 11, fontWeight: 700 }}>P</span>
                  </div>
                  <span className="fm-rr-supply-lbl">Paper</span>
                  <MiniBar value={r.paperLevel} max={100} height={5}
                    color={supplyColor(r.paperLevel)} />
                  <span className="fm-rr-supply-val" style={{ color: supplyColor(r.paperLevel) }}>
                    {r.paperLevel}%
                  </span>
                </div>
                <div className="fm-rr-supply-row">
                  <div className="fm-rr-supply-icon" style={{ color: supplyColor(r.soapLevel) }}>
                    <span style={{ fontSize: 11, fontWeight: 700 }}>S</span>
                  </div>
                  <span className="fm-rr-supply-lbl">Soap</span>
                  <MiniBar value={r.soapLevel} max={100} height={5}
                    color={supplyColor(r.soapLevel)} />
                  <span className="fm-rr-supply-val" style={{ color: supplyColor(r.soapLevel) }}>
                    {r.soapLevel}%
                  </span>
                </div>
              </div>

              {/* Environment + odor */}
              <div className="fm-rr-env-row">
                <div className="fm-rr-env-item">
                  <FiThermometer size={13} color="#ef4444" />
                  <span>{r.temperature}°C</span>
                </div>
                <div className="fm-rr-env-item">
                  <FiDroplet size={13} color="#3b82f6" />
                  <span>{r.humidity}%</span>
                </div>
                <div className="fm-rr-env-item" style={{ color: odorColor(r.odorLevel) }}>
                  <FiWind size={13} />
                  <span>Odor {r.odorLevel}/100</span>
                </div>
              </div>

              {/* Cleaning schedule + usage */}
              <div className="fm-rr-footer">
                <div className="fm-rr-footer-item">
                  <span className="fm-rr-footer-lbl">Last Cleaned</span>
                  <span className="fm-rr-footer-val">{r.lastCleaned}</span>
                </div>
                <div className="fm-rr-footer-item">
                  <span className="fm-rr-footer-lbl">Next Cleaning</span>
                  <span className="fm-rr-footer-val"
                    style={{ color: r.nextCleaning === "Overdue" ? "#ef4444" : r.nextCleaning.includes("5m") ? "#f59e0b" : "#374151" }}>
                    {r.nextCleaning}
                  </span>
                </div>
                <div className="fm-rr-footer-item">
                  <span className="fm-rr-footer-lbl">Daily Usage</span>
                  <span className="fm-rr-footer-val">{r.dailyUsage} visits</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RestroomPanel;
